import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useBriefingState } from '../features/Briefing/BriefingState';
import { StepActiveFunction } from '../features/Briefing/StepActiveFunction';
import { StepActiveIngredient } from '../features/Briefing/StepActiveIngredient';
import { StepAdditionalInfo } from '../features/Briefing/StepAdditionalInfo';
import { StepApplication } from '../features/Briefing/StepApplication';
import { StepColour } from '../features/Briefing/StepColour';
import { StepFormulationAttributes } from '../features/Briefing/StepFormulationAttributes';
import { StepFragrance } from '../features/Briefing/StepFragrance';
import { StepIngredients } from '../features/Briefing/StepIngredients';
import { StepModificationIssue } from '../features/Briefing/StepModificationIssue';
import { StepModificationType } from '../features/Briefing/StepModificationType';
import { StepPackaging } from '../features/Briefing/StepPackaging';
import { StepPrice } from '../features/Briefing/StepPrice';
import { StepProblem } from '../features/Briefing/StepProblem';
import { StepProcessingCapabilities } from '../features/Briefing/StepProcessingCapabilities';
import { StepProductDetails } from '../features/Briefing/StepProductDetails';
import { StepRequirements } from '../features/Briefing/StepRequirements';
import { StepRestrictions } from '../features/Briefing/StepRestrictions';
import { StepReview } from '../features/Briefing/StepReview';
import { StepTargetConsumer } from '../features/Briefing/StepTargetConsumer';
import { capitalise } from '../lib/utils';
import { RouteParams, Step } from '../types';
import { GTMBaseContentViewObject } from './GTMUtils';
import { useGTM } from './useGTM';

export const useBriefingRoute = () => {
  const [briefingState] = useBriefingState();
  const { branch, step } = useParams<RouteParams>();
  const { updateDataLayer } = useGTM();

  const generateTrackingDataBlock = useCallback(
    (content: string) => {
      return {
        ...GTMBaseContentViewObject,
        data: {
          category: capitalise(branch),
          type: briefingState.steps
            ? `Step ${briefingState.steps.indexOf(step || ('' as Step))} / ${
                briefingState.steps.length
              }`
            : '',
          content,
        },
      };
    },
    [step, branch, briefingState.steps],
  );

  const nameMapping = useMemo(
    () => ({
      ['application']: 'Product Application',
      ['target-consumer']: 'Target Consumer',
      ['formulation-attributes']: 'Formulation Attributes',
      ['processing-capabilities']: 'Processing Capabilities',
      ['packaging']: 'Packaging',
      ['product-details']: 'Product Details',
      ['fragrance']: 'Fragrance',
      ['restrictions']: 'Restrictions',
      ['requirements']: 'Requirements',
      ['price']: 'Price',
      ['additional-info']: 'Additional Info',
      ['review']: 'Review',
      ['modification-type']: 'Modification Type',
      ['modification-issue']: 'Modification Issue',
      ['active-ingredients']: 'Active Ingredient',
      ['active-function']: 'Active Function',
      ['problem']: `Problem`,
      ['ingredients']: 'Current Ingredients',
      ['colour']: 'Colour',
    }),
    [],
  );

  useEffect(() => {
    const trackingData = generateTrackingDataBlock(nameMapping[step as Step]);

    updateDataLayer(trackingData);
  }, [generateTrackingDataBlock, nameMapping, step, updateDataLayer]);

  const currentRoute = () => {
    switch (step) {
      case 'application':
        return StepApplication;
      case 'target-consumer':
        return StepTargetConsumer;
      case 'formulation-attributes':
        return StepFormulationAttributes;
      case 'processing-capabilities':
        return StepProcessingCapabilities;
      case 'packaging':
        return StepPackaging;
      case 'product-details':
        return StepProductDetails;
      case 'fragrance':
        return StepFragrance;
      case 'restrictions':
        return StepRestrictions;
      case 'requirements':
        return StepRequirements;
      case 'price':
        return StepPrice;
      case 'additional-info':
        return StepAdditionalInfo;
      case 'review':
        return StepReview;
      case 'modification-type':
        return StepModificationType;
      case 'modification-issue':
        return StepModificationIssue;
      case 'active-ingredients':
        return StepActiveIngredient;
      case 'active-function':
        return StepActiveFunction;
      case 'problem':
        return StepProblem;
      case 'ingredients':
        return StepIngredients;
      case 'colour':
        return StepColour;
      default:
        return null;
    }
  };

  return currentRoute;
};
